import { ExternalLink, Quote, Route, ShieldCheck } from "lucide-react";
import type { Locale } from "@/lib/i18n";
import {
  editorialText,
  getResourceEditorialStrategy,
  type ResourceEditorialStrategy,
} from "@/lib/resource-editorial-strategy";

type ResourceLike = Parameters<typeof getResourceEditorialStrategy>[0];
type CommercialOwner = "plus" | "development";

type CommercialBridge = {
  owner: CommercialOwner;
  href: string;
  label: string;
  body: string;
};

const COMMERCIAL_OWNER_BY_SLUG: Record<string, CommercialOwner> = {
  "shopify-functions": "plus",
  "shopify-checkout-extensibility-2026": "plus",
  "how-to-choose-a-shopify-development-agency": "development",
};

function commercialAuthorityBridge(resource: ResourceLike, locale: Locale): CommercialBridge | null {
  const slug = (resource as any).slug as string | undefined;
  const owner = slug ? COMMERCIAL_OWNER_BY_SLUG[slug] : undefined;
  if (!owner) return null;

  if (owner === "plus") {
    return locale === "es"
      ? {
          owner,
          href: "/es/agencia-shopify-plus",
          label: "Revisar ingeniería Shopify Plus",
          body: "Esta guía cubre la decisión técnica. Para implementar Functions, Checkout Extensibility, B2B, Markets e integraciones con responsabilidad de release, usa la página comercial que posee ese alcance.",
        }
      : {
          owner,
          href: "/shopify-plus-agency",
          label: "Review Shopify Plus engineering",
          body: "This guide covers the technical decision. For implementation across Functions, Checkout Extensibility, B2B, Markets and integrations with release ownership, use the commercial page that owns that scope.",
        };
  }

  return locale === "es"
    ? {
        owner,
        href: "/es/desarrollo-shopify",
        label: "Revisar desarrollo Shopify",
        body: "Esta guía ayuda a evaluar proveedores. Para alcance, implementación, QA y entrega de un proyecto real, continúa con la página comercial de desarrollo Shopify.",
      }
    : {
        owner,
        href: "/shopify-experts",
        label: "Review Shopify development",
        body: "This guide helps evaluate providers. For scope, implementation, QA and delivery of an actual Shopify project, continue to the commercial development owner.",
      };
}

function normalizeInternalHref(href: string) {
  const replacements: Record<string, string> = {
    "/resources/shopify-plus-migration-2026": "/resources/shopify-plus-migration",
    "/es/recursos/shopify-plus-migration-2026": "/es/recursos/shopify-plus-migration",
    "/resources/shopify-product-recommender-liquid": "/resources/shopify-liquid-product-recommender",
    "/es/recursos/shopify-product-recommender-liquid": "/es/recursos/shopify-liquid-product-recommender",
  };
  return replacements[href] ?? href;
}

export function ArticleEditorialContext({
  resource,
  locale,
}: {
  resource: ResourceLike;
  locale: Locale;
}) {
  const strategy = getResourceEditorialStrategy(resource);
  const paths = strategy.paths?.[locale] ?? [];
  const commercialBridge = commercialAuthorityBridge(resource, locale);

  return (
    <section
      className="institutional-reading-brief-continuation mx-auto max-w-6xl px-6"
      aria-label={locale === "es" ? "Resumen de la guía" : "Guide summary"}
    >
      <div className="institutional-reading-brief overflow-hidden rounded-b-[1.75rem] border">
        <div className="grid gap-px bg-[var(--line-neutral)] md:grid-cols-2">
          <ContextCell
            icon={Route}
            eyebrow={locale === "es" ? "Qué cubre esta guía" : "What this guide covers"}
            body={editorialText(strategy.focus, locale) ?? ""}
          />
          <ContextCell
            icon={ShieldCheck}
            eyebrow={locale === "es" ? "Para quién está escrita" : "Who this is for"}
            body={editorialText(strategy.audience, locale) ?? ""}
          />
        </div>

        {strategy.status ? (
          <div className="border-t border-[var(--line-neutral)] bg-[var(--surface-methodology)] p-5">
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
              {locale === "es" ? "Nota de vigencia" : "Freshness note"}
            </p>
            <p className="mt-2 text-sm leading-relaxed text-foreground/78">
              {editorialText(strategy.status, locale)}
            </p>
          </div>
        ) : null}

        {paths.length > 0 ? (
          <div className="border-t border-[var(--line-neutral)] bg-[var(--surface-editorial)] p-5">
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
              {locale === "es" ? "Guías y siguientes pasos relacionados" : "Related guides and next steps"}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {paths.map((path) => (
                <a
                  key={path.href}
                  href={normalizeInternalHref(path.href)}
                  className="rounded-full border border-[var(--line-neutral)] px-4 py-2 text-xs font-semibold text-foreground transition-colors hover:border-foreground/30 hover:bg-[var(--surface-secondary)]"
                >
                  {path.label} →
                </a>
              ))}
            </div>
          </div>
        ) : null}

        {commercialBridge ? (
          <div
            className="border-t border-[var(--line-neutral)] bg-[var(--surface-secondary)] p-5 sm:flex sm:items-center sm:justify-between sm:gap-6"
            data-seo-commercial-owner={commercialBridge.owner}
          >
            <div className="max-w-3xl">
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                {locale === "es" ? "Ruta de implementación" : "Implementation path"}
              </p>
              <p className="mt-2 text-sm leading-relaxed text-foreground/82">{commercialBridge.body}</p>
            </div>
            <a
              href={commercialBridge.href}
              className="mt-4 inline-flex shrink-0 items-center rounded-full border border-foreground/20 px-4 py-2 text-xs font-semibold text-foreground transition-colors hover:border-foreground/40 hover:bg-[var(--surface-editorial)] sm:mt-0"
            >
              {commercialBridge.label} →
            </a>
          </div>
        ) : null}
      </div>
    </section>
  );
}

function ContextCell({
  icon: Icon,
  eyebrow,
  body,
}: {
  icon: typeof Route;
  eyebrow: string;
  body: string;
}) {
  return (
    <div className="bg-[var(--surface-editorial)] p-6">
      <Icon className="h-4 w-4 text-muted-foreground" aria-hidden />
      <p className="mt-4 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">{eyebrow}</p>
      <p className="mt-2 text-sm leading-relaxed text-foreground/82">{body}</p>
    </div>
  );
}

export function ArticleVerifiedSources({
  strategy,
  locale,
}: {
  strategy: ResourceEditorialStrategy;
  locale: Locale;
}) {
  return (
    <section className="mt-14 rounded-[1.75rem] border border-[var(--line-neutral)] bg-[var(--surface-secondary)] p-6 sm:p-8">
      <div className="flex items-start gap-3">
        <Quote className="mt-0.5 h-5 w-5 text-muted-foreground" aria-hidden />
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
            {locale === "es" ? "Fuentes verificadas" : "Verified primary sources"}
          </p>
          <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight text-foreground">
            {locale === "es" ? "Documentación utilizada para validar esta guía" : "Documentation used to validate this guide"}
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
            {locale === "es"
              ? "Las capacidades de plataforma, fechas y recomendaciones técnicas deben contrastarse con fuentes oficiales. Estos enlaces salen del sitio de Shugert y abren la documentación original."
              : "Platform capabilities, dates, and technical recommendations should be checked against first-party documentation. These links leave Shugert and open the original source."}
          </p>
        </div>
      </div>
      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        {strategy.sources.map((source) => (
          <a
            key={source.href}
            href={source.href}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center justify-between rounded-xl border border-[var(--line-neutral)] bg-[var(--surface-editorial)] px-4 py-3 text-sm transition-colors hover:border-foreground/30"
          >
            <span>
              <span className="block font-medium text-foreground">{source.label}</span>
              <span className="mt-0.5 block text-xs text-muted-foreground">{source.publisher}</span>
            </span>
            <ExternalLink className="h-4 w-4 text-muted-foreground transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" aria-hidden />
          </a>
        ))}
      </div>
    </section>
  );
}

export function LinkableResourcePrompt({
  strategy,
  locale,
  title,
  url,
}: {
  strategy: ResourceEditorialStrategy;
  locale: Locale;
  title: string;
  url: string;
}) {
  if (!strategy.linkable) return null;
  return (
    <section className="mt-10 rounded-[1.75rem] border border-white/15 bg-[var(--surface-authority)] p-7 text-white sm:p-9">
      <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-white/58">
        {locale === "es" ? "Úsalo y cítalo" : "Use it and reference it"}
      </p>
      <h2 className="mt-3 max-w-2xl font-display text-2xl font-semibold tracking-tight sm:text-3xl">
        {locale === "es"
          ? "¿Construiste algo con esta guía? Comparte el resultado y enlaza la referencia que te ayudó."
          : "Built something with this guide? Share the result and link to the reference that helped you."}
      </h2>
      <p className="mt-4 max-w-2xl text-sm leading-relaxed text-white/72">
        {locale === "es"
          ? "Los ejemplos reales, repositorios, notas técnicas y traducciones útiles hacen que el ecosistema sea mejor. Puedes citar esta página con el título y URL de abajo, o seguir explorando los proyectos relacionados."
          : "Real examples, repositories, technical notes, and useful translations make the ecosystem better. You may cite this page using the title and URL below, or continue through the related projects."}
      </p>
      <div className="mt-6 rounded-xl border border-white/15 bg-white/[0.06] p-4 font-mono text-xs leading-relaxed text-white/88">
        {title}: {url}
      </div>
    </section>
  );
}
